import React, { useEffect, useRef, useState } from 'react';
import Icon from '../AppIcon';

const MAX_ASSOLUTO = 999999999;

const arrotonda = (n, decimali) => {
  const f = 10 ** decimali;
  return Math.round((Number(n) + Number.EPSILON) * f) / f;
};

const formatta = (n, decimali) => (
  n === null || n === undefined || Number.isNaN(Number(n)) ? '' : String(arrotonda(n, decimali))
);

/**
 * Stepper di quantità nello stile del modal "Dettagli Articolo" CCO:
 * due pulsanti tondi (− rosso, + verde), numero grande al centro, anteprima della differenza.
 *
 * - Pulsanti: ±1 (passo), mai sotto `min` né sopra `max`.
 * - Inserimento manuale: il numero è un campo di testo; i decimali ammessi dipendono dall'unità di misura
 *   (decimali = 0 → solo interi; 2 → fino a due cifre, es. metri).
 * - Delta: se passi `originale` mostra "+N rispetto all'originale (X)".
 *
 * Props: value (numero), onChange(numero), originale, min=0, max, decimali=0, passo=1,
 *        colore (classe testo del numero), unitaLabel, disabilitato, etichettaDelta, nome.
 */
const QtyStepper = ({
  value,
  onChange,
  originale,
  min = 0,
  max = MAX_ASSOLUTO,
  decimali = 0,
  passo = 1,
  colore = 'text-foreground',
  unitaLabel,
  disabilitato = false,
  etichettaDelta = 'rispetto all\'originale',
  nome = 'qta',
}) => {
  const [testo, setTesto] = useState(formatta(value, decimali));
  const inFocus = useRef(false);

  // allinea il testo quando il valore cambia dall'esterno (pulsanti, reset)
  useEffect(() => {
    if (!inFocus.current) setTesto(formatta(value, decimali));
  }, [value, decimali]);

  const numero = Number(value) || 0;

  const applica = (n) => {
    const v = Math.min(max, Math.max(min, arrotonda(n, decimali)));
    onChange(v);
    setTesto(formatta(v, decimali));
  };

  const alCambio = (e) => {
    const grezzo = e.target.value.replace(',', '.');
    const ammesso = decimali > 0
      ? new RegExp(`^\\d*(\\.\\d{0,${decimali}})?$`)
      : /^\d*$/;
    if (!ammesso.test(grezzo)) return;
    setTesto(grezzo);
    if (grezzo === '' || grezzo === '.') return;
    const n = Number(grezzo);
    if (Number.isFinite(n)) onChange(Math.min(max, n));
  };

  const alBlur = () => {
    inFocus.current = false;
    applica(testo === '' || testo === '.' ? min : Number(testo));
  };

  const delta = originale === undefined || originale === null
    ? 0
    : arrotonda(numero - Number(originale), decimali);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => applica(numero - passo)}
          disabled={disabilitato || numero <= min}
          aria-label="Diminuisci quantità"
          className="w-8 h-8 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-red-500/20 disabled:hover:text-red-500"
        >
          <Icon name="Minus" size={16} strokeWidth={3} />
        </button>

        <div className="flex items-baseline gap-2">
          <input
            name={nome}
            type="text"
            inputMode={decimali > 0 ? 'decimal' : 'numeric'}
            value={testo}
            onChange={alCambio}
            onFocus={(e) => { inFocus.current = true; e.target.select(); }}
            onBlur={alBlur}
            disabled={disabilitato}
            aria-label="Quantità"
            style={{ width: `${Math.max(2, testo.length + 0.5)}ch` }}
            className={`min-w-[2ch] max-w-[12ch] bg-transparent outline-none text-center text-5xl font-black tabular-nums border-b-2 border-transparent focus:border-primary/40 transition-colors ${colore} disabled:opacity-50`}
          />
          {unitaLabel && (
            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">{unitaLabel}</span>
          )}
        </div>

        <button
          type="button"
          onClick={() => applica(numero + passo)}
          disabled={disabilitato || numero >= max}
          aria-label="Aumenta quantità"
          className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-emerald-500/20 disabled:hover:text-emerald-500"
        >
          <Icon name="Plus" size={16} strokeWidth={3} />
        </button>
      </div>

      {delta !== 0 && (
        <span className={`text-[10px] font-black mt-1 ${delta > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
          {delta > 0 ? `+${delta}` : delta} {etichettaDelta} ({formatta(originale, decimali)})
        </span>
      )}
    </div>
  );
};

export default QtyStepper;
